import { Check, ChevronDown, X } from 'lucide-react';
import React, { useEffect, useState, useRef } from 'react';
import { Button } from 'src/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'src/components/ui/dropdown-menu';
import { useReportViewCustomize } from '../manage-client-views';

export const DEFAULT_FIELDS = ['clicks', 'impressions', 'conversions', 'spend', 'cpa', 'cpc', 'cpm', 'ctr'];

interface ClientViewCustomizeWrapperProps {
  key?: string;
  className?: string;
  enabled?: boolean;
  bottom?: boolean;
  children: React.ReactNode;
  fieldSelection?: boolean;
  defaults?: boolean;
  maxDefaults?: number;
  onStateChange?: (enabled: boolean) => void;
  onFieldSelectionChange?: (selectedFields: string[]) => void;
  onDefaultSelectionChange?: (selectedDefaultTypes: string[]) => void;
}

const ClientViewCustomizeWrapper: React.FC<ClientViewCustomizeWrapperProps> = ({
  children,
  enabled,
  onStateChange,
  fieldSelection = true,
  defaults = true,
  maxDefaults = 1,
  key,
  className,
  bottom,
  onFieldSelectionChange,
  onDefaultSelectionChange,
}) => {
  const customizeContext = useReportViewCustomize();
  const [enabledState, setEnabledState] = useState(enabled);
  const [fieldSelectionState, setFieldSelectionState] = useState<string[]>([]);
  const [defaultSelectionState, setDefaultSelectionState] = useState<string[]>([]);

  // Use refs to track previous values and callbacks to avoid infinite loops
  const prevEnabledStateRef = useRef(enabledState);
  const prevFieldSelectionStateRef = useRef<string[]>([]);
  const prevDefaultSelectionStateRef = useRef<string[]>([]);
  const onStateChangeRef = useRef(onStateChange);
  const onFieldSelectionChangeRef = useRef(onFieldSelectionChange);
  const onDefaultSelectionChangeRef = useRef(onDefaultSelectionChange);

  // Update refs when callbacks change
  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  useEffect(() => {
    onFieldSelectionChangeRef.current = onFieldSelectionChange;
  }, [onFieldSelectionChange]);

  useEffect(() => {
    onDefaultSelectionChangeRef.current = onDefaultSelectionChange;
  }, [onDefaultSelectionChange]);

  // Only call onStateChange when enabledState actually changes
  useEffect(() => {
    if (prevEnabledStateRef.current !== enabledState) {
      prevEnabledStateRef.current = enabledState;
      onStateChangeRef.current?.(enabledState ?? false);
    }
  }, [enabledState]);

  // Only call onFieldSelectionChange when fieldSelectionState actually changes
  useEffect(() => {
    const prevFields = prevFieldSelectionStateRef.current;
    const currentFields = fieldSelectionState;

    // Compare arrays to see if they actually changed
    const fieldsChanged =
      prevFields.length !== currentFields.length ||
      prevFields.some((field, index) => field !== currentFields[index]) ||
      currentFields.some((field, index) => field !== prevFields[index]);

    if (fieldsChanged) {
      prevFieldSelectionStateRef.current = [...currentFields];
      onFieldSelectionChangeRef.current?.(currentFields);
    }
  }, [fieldSelectionState]);

  useEffect(() => {
    const prevDefaults = prevDefaultSelectionStateRef.current;
    const currentDefaults = defaultSelectionState;

    // Compare arrays to see if they actually changed
    const defaultsChanged =
      prevDefaults.length !== currentDefaults.length ||
      prevDefaults.some((defaultType: string, index: number) => defaultType !== currentDefaults[index]) ||
      currentDefaults.some((defaultType: string, index: number) => defaultType !== prevDefaults[index]);

    if (defaultsChanged) {
      prevDefaultSelectionStateRef.current = [...currentDefaults];
      onDefaultSelectionChangeRef.current?.(currentDefaults);
    }
  }, [defaultSelectionState]);

  const handleItemClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!enabledState) {
      e.stopPropagation();
      e.preventDefault();
      return false; // Add explicit return false
    }
  };

  const handleFieldSelection = (field: string) => {
    const newSelection = fieldSelectionState.includes(field)
      ? fieldSelectionState.filter((f) => f !== field)
      : [...fieldSelectionState, field];
    setFieldSelectionState(newSelection);
  };

  const handleDefaultSelection = (defaultType: string) => {
    if (defaultSelectionState.includes(defaultType)) {
      const newSelection = defaultSelectionState.filter((d) => d !== defaultType);
      setDefaultSelectionState(newSelection);
      return;
    }

    if (defaultSelectionState.length >= maxDefaults) {
      return;
    } else {
      const newSelection = [...defaultSelectionState, defaultType];
      setDefaultSelectionState(newSelection);
    }
  };

  return (
    <div className={`relative ${className || ''}`}>
      <div className={`bg-gray-500/50 rounded-md p-2 flex flex-col gap-2 absolute ${bottom ? 'bottom-2' : 'top-2'} right-2 z-[9999]`}>
        <Button
          className={`text-white ${enabledState ? 'bg-green-501' : 'bg-red-501'} rounded-full`}
          variant='primary'
          size='sm'
          onClick={() => setEnabledState(!enabledState)}
        >
          {enabledState ? <Check className='size-4' /> : <X className='size-4' />}
          {enabledState ? 'Enabled' : 'Disabled'}
        </Button>
        {fieldSelection && enabledState && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className='text-black bg-gray-201 rounded-full justify-start'
                variant='primary'
                size='sm'
              >
                <ChevronDown className='size-4' />
                Fields
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='z-[9999]'>
              {DEFAULT_FIELDS.map((field) => (
                <DropdownMenuItem
                  key={field}
                  onClick={(e) => {
                    e.preventDefault();
                    handleFieldSelection(field);
                  }}
                  onSelect={(e) => e.preventDefault()}
                  className='flex flex-row items-center justify-between'
                >
                  {field}
                  {(fieldSelectionState.includes(field) || fieldSelectionState.length === 0) && <Check className='size-4' />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {defaults && enabledState && maxDefaults > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className='text-black bg-gray-201 rounded-full justify-start'
                variant='primary'
                size='sm'
              >
                <ChevronDown className='size-4' />
                Defaults
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='z-[9999]'>
              {DEFAULT_FIELDS.map((field) => (
                <DropdownMenuItem
                  key={field}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDefaultSelection(field);
                  }}
                  onSelect={(e) => e.preventDefault()}
                  className='flex flex-row items-center justify-between'
                >
                  {field}
                  {defaultSelectionState.includes(field) && (
                    <span className='text-xs text-gray-500 text-right'>
                      {defaultSelectionState.indexOf(field) + 1}
                    </span>
                  )
                  }
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div
        className={`w-full [&>*]:w-full ${enabledState ? 'opacity-100' : 'opacity-30'} ${!enabledState ? 'pointer-events-none' : ''}`}
        onClick={handleItemClick}
      >
        {children}
      </div>
    </div>
  );
};

export default ClientViewCustomizeWrapper;
